import { useState } from 'react';

import { AssetDTO, formatDateWithUserTimezone, FormDTO, FormType, IssueDTO, IssueSeverity, IssueSource, IssueStatus } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { match } from 'ts-pattern';

import { PlusIcon } from '@/components/icons/plus';
import WarningIcon from '@/components/icons/warning';
import IssueDrawer from '@/components/issues/issue-drawer';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useUserStore } from '@/stores/user.store';
import { getIssueSeverityColorClass } from '@/utils/issue';

interface IssuesDisplayProps {
  asset: AssetDTO;
  form: FormDTO;
  checkId: string;
  locationId?: string;
  onIssuesChange?: (newIssue: IssueDTO) => void;
  onBlur?: () => void;
  readonly?: boolean;
  requirePhotos?: boolean;
  showNewIssueButton?: boolean;
  issues: IssueDTO[];
  fieldReference?: string;
}

export function IssuesDisplay({
  asset,
  form,
  checkId,
  locationId,
  readonly = false,
  requirePhotos = false,
  showNewIssueButton = true,
  issues,
  onIssuesChange,
  fieldReference,
}: IssuesDisplayProps) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const [addIssueDrawerOpen, setAddIssueDrawerOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<IssueDTO | undefined>(undefined);

  const handleAddIssueComplete = (issue: IssueDTO) => {
    setAddIssueDrawerOpen(false);

    if (onIssuesChange) {
      onIssuesChange(issue);
    }
  };

  if (issues.length === 0 && !showNewIssueButton) {
    return null;
  }

  return (
    <>
      <div className="rounded-10 bg-grey-1 p-[20px]">
        <Label>{issues.length > 0 ? 'Open Issues' : 'No Open Issues'}</Label>
        <div className="mt-[10px] flex flex-col gap-[10px]">
          {issues
            .filter((issue) => (readonly ? true : issue.status === IssueStatus.Open))
            .map((issue) => {
              const issueSeverityColorClass = getIssueSeverityColorClass(issue.severity as IssueSeverity);
              const issueSeverityIcon =
                issue.severity === IssueSeverity.Critical ? (
                  <WarningIcon size={20} className="text-default-red" />
                ) : (
                  <div className={cn('mx-[3px] h-[15px] w-[15px] rounded-full border-[3px]', issueSeverityColorClass)} />
                );

              return (
                <div
                  key={issue.id}
                  className="rounded-10 flex items-start gap-2 bg-white p-[10px] shadow-sm"
                  onClick={() => {
                    setSelectedIssue(issue);
                    setAddIssueDrawerOpen(true);
                  }}>
                  <div className="flex flex-col gap-[5px]">
                    <div className="flex items-center gap-[5px]">
                      <div className="pb-[2px]">{issueSeverityIcon}</div>
                      <div className="text-sm font-normal">{issue.title || issue.description}</div>
                    </div>
                    <div className="text-secondary-text text-xs">Created: {formatDateWithUserTimezone(new Date(issue.createdAt), userTimezone)}</div>
                  </div>
                </div>
              );
            })}

          {showNewIssueButton && (
            <Button
              type="button"
              variant="default"
              disabled={readonly}
              onClick={() => {
                setSelectedIssue(undefined);
                setAddIssueDrawerOpen(true);
              }}
              className="mt-[20px] w-full gap-[5px] rounded-[5px]">
              <PlusIcon className="size-4" />
              Create Issue
            </Button>
          )}
        </div>
      </div>

      <IssueDrawer
        noTrigger
        issueId={selectedIssue?.id}
        open={addIssueDrawerOpen}
        onOpenChange={setAddIssueDrawerOpen}
        asset={asset}
        callback={handleAddIssueComplete}
        requirePhotos={requirePhotos}
        source={match(form.type)
          .with(FormType.InspectionForm, () => IssueSource.Inspection)
          .with(FormType.ScheduleForm, () => IssueSource.Service)
          .otherwise(() => IssueSource.Manual)}
        checkFormElementId={checkId}
        locationId={locationId}
        fieldReference={fieldReference}
        largeButtons
      />
    </>
  );
}
